import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { activitiesAPI, groupsAPI, connectionsAPI } from '../api/client';
import Layout from '../components/Layout';
import '../styles/create-activity.css';

export default function CreateActivity() {
  const [formData, setFormData] = useState({
    activity_type: 'run',
    title: '',
    description: '',
    start_time: '',
    duration_minutes: 60,
    location_name: '',
    visibility_level: 'friend_circle',
    invited_group_id: '',
  });
  const [groups, setGroups] = useState([]);
  const [friends, setFriends] = useState([]);
  const [invitedFriendIds, setInvitedFriendIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('userId');

  React.useEffect(() => {
    loadGroups();
    loadFriends();
  }, []);

  const loadGroups = async () => {
    try {
      const response = await groupsAPI.getAll();
      setGroups(response.data || []);
    } catch {}
  };

  const loadFriends = async () => {
    try {
      const res = await connectionsAPI.getAll();
      const accepted = (res.data || []).filter(c => c.status === 'accepted');
      setFriends(accepted.map(c => ({
        id: c.user_id === currentUserId ? c.connected_user_id : c.user_id,
        name: c.display_name,
      })));
    } catch {}
  };

  const toggleFriend = (id) => {
    setInvitedFriendIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const dataToSubmit = {
        ...formData,
        duration_minutes: parseInt(formData.duration_minutes, 10),
        // 'group_only' is a UI concept — backend uses 'private' + invited_group_id
        visibility_level: formData.visibility_level === 'group_only' ? 'private' : formData.visibility_level,
        invited_friend_ids: invitedFriendIds.length > 0 ? invitedFriendIds : undefined,
      };

      if (!dataToSubmit.start_time) {
        delete dataToSubmit.start_time;
      }

      if (!dataToSubmit.invited_group_id) {
        delete dataToSubmit.invited_group_id;
      }

      const response = await activitiesAPI.create(dataToSubmit);
      navigate(`/activity/${response.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create activity');
    } finally {
      setLoading(false);
    }
  };

  const activityTypes = [
    { value: 'run', label: 'Run' },
    { value: 'coffee', label: 'Coffee' },
    { value: 'hike', label: 'Hike' },
    { value: 'museum', label: 'Museum' },
    { value: 'cycling', label: 'Cycling' },
    { value: 'dinner', label: 'Dinner' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <Layout title="New Plan">
    <div className="create-activity-container">
      <div className="create-activity-card">
        <h1>Create Activity</h1>
        <p className="subtitle">Share what you're doing anyway.</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Activity Type *</label>
            <select
              name="activity_type"
              value={formData.activity_type}
              onChange={handleChange}
              required
            >
              {activityTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Easy morning run"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Add any details..."
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>When</label>
              <input
                type="datetime-local"
                name="start_time"
                value={formData.start_time}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Duration (minutes)</label>
              <input
                type="number"
                name="duration_minutes"
                value={formData.duration_minutes}
                onChange={handleChange}
                min={10}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              name="location_name"
              value={formData.location_name}
              onChange={handleChange}
              placeholder="e.g., Crissy Field"
            />
          </div>

          <div className="form-group">
            <label>Who can see this?</label>
            <select
              name="visibility_level"
              value={formData.visibility_level}
              onChange={handleChange}
            >
              <option value="private">Only me</option>
              <option value="friend_circle">My friends</option>
              <option value="public">Everyone</option>
              {groups.length > 0 && (
                <option value="group_only">A specific group</option>
              )}
            </select>
          </div>

          {formData.visibility_level === 'group_only' && (
            <div className="form-group">
              <label>Which group?</label>
              <select
                name="invited_group_id"
                value={formData.invited_group_id}
                onChange={handleChange}
                required
              >
                <option value="">Select a group…</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {friends.length > 0 && (
            <div className="form-group">
              <label>Invite specific friends <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span></label>
              <div className="ca-friend-picker">
                {friends.map(f => (
                  <button
                    key={f.id}
                    type="button"
                    className={`ca-friend-chip${invitedFriendIds.includes(f.id) ? ' selected' : ''}`}
                    onClick={() => toggleFriend(f.id)}
                  >
                    {f.name}
                  </button>
                ))}
              </div>
              {invitedFriendIds.length > 0 && (
                <p className="ca-invite-note">They'll get a personal notification and email invite.</p>
              )}
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/feed')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Activity'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </Layout>
  );
}
